<template>
  <label :aria-label="label" class="group flex items-center gap-12">
    <input
      :checked="isChecked"
      class="peer sr-only"
      :name="value"
      v-bind="inputAttrs"
      type="checkbox"
      @change="handleChange"
    />
    <span :class="chipClasses">
      <component :is="icon" :class="iconClasses" />
      <span>
        <span>{{ label }}</span>
        <span v-if="count" :class="countClasses"> ({{ count }}) </span>
      </span>
    </span>
  </label>
</template>

<script lang="ts" setup>
import { type Component, computed, useAttrs } from 'vue'

import { tw } from '@backmarket/utils/string/tw'

const attrs = useAttrs()
const modelValue = defineModel<string[]>({ default: () => [] })

const { variant = 'neutral', value } = defineProps<{
  icon: Component
  variant?: 'positive' | 'negative' | 'neutral'
  label: string
  count?: number
  value: string
}>()

const inputAttrs = computed(() => {
  const { id, ...rest } = attrs

  return rest
})

const isChecked = computed(() => {
  return modelValue.value?.includes(value) ?? false
})

function handleChange(event: Event) {
  const target = event.target as HTMLInputElement
  const currentValue = modelValue.value ?? []

  if (target.checked) {
    modelValue.value = [...currentValue, value]
  } else {
    modelValue.value = currentValue.filter((v) => v !== value)
  }
}

const iconClasses = computed(() => ({
  'text-static-success-hi': variant === 'positive',
  'text-static-default-low': variant === 'neutral',
  'text-static-danger-hi': variant === 'negative',
  'text-static-light-hi': isChecked.value,
}))

const countClasses = computed(() => [
  tw`caption ml-2`,
  isChecked.value ? tw`text-static-light-hi` : tw`text-static-default-low`,
])

const chipClasses = computed(() => [
  isChecked.value
    ? tw`bg-action-default-low-pressed border-action-default-low-pressed text-onaction-default-low-pressed hover:bg-action-default-low-hoverpressed focus-visible:bg-action-default-low-hoverpressed`
    : tw`bg-action-default-low border-action-default-low text-onaction-default-low hover:bg-action-default-low-hover focus-visible:bg-action-default-low-hover`,
  tw`outline-2 outline-offset-4 focus-visible:outline-default-hi`,
  tw`motion-safe:transition motion-safe:duration-[225ms]`,
  tw`cursor-pointer rounded-full border-1 label-large h-40 flex items-center gap-4 px-16`,
])
</script>
